import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    auth, RecaptchaVerifier, PhoneAuthProvider,
    PhoneMultiFactorGenerator, multiFactor
} from './firebaseConfig';
import {
    Box, Typography, Card, CardContent, Button, TextField, Alert,
    Chip, CircularProgress, Fade, Grid, Divider, List, ListItem,
    ListItemIcon, ListItemText, ListItemSecondaryAction, IconButton
} from '@mui/material';
import {
    Security, PhoneAndroid, VerifiedUser, Delete, Add, Shield, CheckCircle
} from '@mui/icons-material';

const SecuritySettings = ({ embedded = false }) => {
    const recaptchaVerifierRef = useRef(null);
    const [user, setUser] = useState(auth.currentUser);
    const [enrolledFactors, setEnrolledFactors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [showEnroll, setShowEnroll] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationId, setVerificationId] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [enrollStep, setEnrollStep] = useState('phone');

    const refreshFactors = useCallback(() => {
        const currentUser = auth.currentUser;
        if (currentUser) {
            setUser(currentUser);
            setEnrolledFactors(multiFactor(currentUser).enrolledFactors || []);
        }
    }, []);

    useEffect(() => { refreshFactors(); }, [refreshFactors]);

    const setupRecaptcha = useCallback(() => {
        if (recaptchaVerifierRef.current) {
            try { recaptchaVerifierRef.current.clear(); } catch (e) { /* ignore */ }
        }
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-settings', {
            size: 'invisible',
            callback: () => {}
        });
    }, []);

    const handleSendCode = async () => {
        if (!phoneNumber || phoneNumber.length < 10) {
            setError('Enter a valid phone number with country code (e.g. +15551234567)');
            return;
        }
        setLoading(true);
        setError('');
        setupRecaptcha();
        try {
            const session = await multiFactor(user).getSession();
            const phoneProvider = new PhoneAuthProvider(auth);
            const vId = await phoneProvider.verifyPhoneNumber(
                { phoneNumber, session },
                recaptchaVerifierRef.current
            );
            setVerificationId(vId);
            setEnrollStep('code');
            setSuccess(`Verification code sent to ${phoneNumber}`);
        } catch (err) {
            setError('Failed to send code: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyAndEnroll = async () => {
        if (!verificationCode || verificationCode.length < 6) {
            setError('Enter the 6-digit code');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
            const assertion = PhoneMultiFactorGenerator.assertion(cred);
            await multiFactor(user).enroll(assertion, 'Phone Number');
            setSuccess('Phone verification enrolled successfully!');
            setShowEnroll(false);
            setPhoneNumber('');
            setVerificationCode('');
            setEnrollStep('phone');
            refreshFactors();
        } catch (err) {
            setError('Enrollment failed: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUnenroll = async (factor) => {
        if (!window.confirm(`Remove ${factor.displayName || 'this factor'}? You'll need to re-enroll to use MFA.`)) return;
        setLoading(true);
        setError('');
        try {
            await multiFactor(user).unenroll(factor);
            setSuccess('Factor removed successfully.');
            refreshFactors();
        } catch (err) {
            setError('Failed to remove factor: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fade in timeout={600}>
            <Box sx={{
                maxWidth: embedded ? 'none' : 800,
                mx: embedded ? 0 : 'auto',
                p: embedded ? 0 : { xs: 2, md: 4 },
            }}>
                {!embedded && (
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
                            Security Settings
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Manage your multi-factor authentication and account security
                        </Typography>
                    </Box>
                )}

                {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 3 }}>{success}</Alert>}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card sx={{
                            borderRadius: 3, overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                        }}>
                            <Box sx={{
                                background: 'linear-gradient(135deg, #1a237e, #4a148c)',
                                p: 3, color: 'white', display: 'flex', alignItems: 'center', gap: 2
                            }}>
                                <Shield sx={{ fontSize: 36 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Multi-Factor Authentication
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                        {enrolledFactors.length > 0
                                            ? 'MFA is active — your account is protected'
                                            : 'MFA is not enabled — add a phone number to secure your account'}
                                    </Typography>
                                </Box>
                                <Box sx={{ ml: 'auto' }}>
                                    <Chip
                                        icon={enrolledFactors.length > 0 ? <CheckCircle /> : <Security />}
                                        label={enrolledFactors.length > 0 ? 'Enabled' : 'Not Enabled'}
                                        sx={{
                                            backgroundColor: enrolledFactors.length > 0
                                                ? 'rgba(67,233,123,0.2)' : 'rgba(255,255,255,0.2)',
                                            color: 'white', fontWeight: 600
                                        }}
                                    />
                                </Box>
                            </Box>

                            <CardContent sx={{ p: 3 }}>
                                {enrolledFactors.length > 0 ? (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                                            ENROLLED FACTORS
                                        </Typography>
                                        <List disablePadding>
                                            {enrolledFactors.map((factor, idx) => (
                                                <ListItem key={idx} sx={{
                                                    border: '1px solid', borderColor: 'divider',
                                                    borderRadius: 2, mb: 1, px: 2
                                                }}>
                                                    <ListItemIcon>
                                                        <PhoneAndroid sx={{ color: '#667eea' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={factor.displayName || 'Phone Number'}
                                                        secondary={factor.phoneNumber || `Enrolled ${factor.enrollmentTime ? new Date(factor.enrollmentTime).toLocaleDateString() : ''}`}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton edge="end" onClick={() => handleUnenroll(factor)}
                                                            disabled={loading} color="error" size="small">
                                                            <Delete />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Security sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                            No second factor enrolled yet
                                        </Typography>
                                    </Box>
                                )}

                                <Divider sx={{ my: 3 }} />

                                {!showEnroll ? (
                                    <Button
                                        variant="outlined" startIcon={<Add />}
                                        onClick={() => { setShowEnroll(true); setEnrollStep('phone'); setError(''); }}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {enrolledFactors.length > 0 ? 'Add Another Phone' : 'Enroll Phone Number'}
                                    </Button>
                                ) : (
                                    <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
                                        {enrollStep === 'phone' && (
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                                    Add Phone for MFA
                                                </Typography>
                                                <TextField
                                                    fullWidth autoFocus label="Phone Number" value={phoneNumber}
                                                    onChange={e => setPhoneNumber(e.target.value)}
                                                    placeholder="+1 (555) 123-4567" sx={{ mb: 2 }}
                                                    helperText="Include country code (e.g. +1 for US)"
                                                />
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button variant="contained" onClick={handleSendCode}
                                                        disabled={loading || phoneNumber.length < 10}
                                                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PhoneAndroid />}
                                                        sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: 2 }}>
                                                        Send Code
                                                    </Button>
                                                    <Button variant="text" onClick={() => { setShowEnroll(false); setError(''); }}
                                                        sx={{ borderRadius: 2 }}>
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}

                                        {enrollStep === 'code' && (
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                                    Enter Verification Code
                                                </Typography>
                                                <TextField
                                                    fullWidth autoFocus label="6-digit Code" value={verificationCode}
                                                    onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                    inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.3rem', letterSpacing: 6 } }}
                                                    sx={{ mb: 2 }}
                                                    onKeyDown={e => e.key === 'Enter' && handleVerifyAndEnroll()}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button variant="contained" onClick={handleVerifyAndEnroll}
                                                        disabled={loading || verificationCode.length < 6}
                                                        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VerifiedUser />}
                                                        sx={{ background: 'linear-gradient(135deg, #43e97b, #38f9d7)', color: '#1a237e', borderRadius: 2 }}>
                                                        Verify & Enroll
                                                    </Button>
                                                    <Button variant="text" onClick={() => { setEnrollStep('phone'); setError(''); }}
                                                        sx={{ borderRadius: 2 }}>
                                                        Back
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Card>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Account Info</Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Email:</strong> {user?.email || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Display Name:</strong> {user?.displayName || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Provider:</strong> Google
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Last Sign-In:</strong> {user?.metadata?.lastSignInTime || 'N/A'}
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                <div id="recaptcha-settings" />
            </Box>
        </Fade>
    );
};

export default SecuritySettings;
